<template lang="pug">
  .product-list(
    v-element-visibility="(isVisible) => show(isVisible)"
    :key="podId + uri + widget"
    :class="size ? 'img-size-'+size : []"
    :data-cnstrc-recommendations="isRecommendations || undefined"
    :data-cnstrc-recommendations-pod-id="isRecommendations ? podId : undefined"
    :data-cnstrc-recommendations-seed-items="isRecommendations && itemIds?.length ? itemIds.join(',') : undefined"
    :data-cnstrc-browse="groupId ? true : undefined"
    :data-cnstrc-filter-name="groupId ? 'group_id' : undefined"
    :data-cnstrc-filter-value="groupId || undefined"
    :data-cnstrc-result-id="cnstrcResultId || undefined"
    :data-cnstrc-num-results="cnstrcNumResults"
  )
    .product-card(
      v-for="(product, index) in productList"
      :data-cnstrc-item="isRecommendations ? 'recommendation' : undefined"
      :data-cnstrc-item-id="isRecommendations ? product.productId : undefined"
      :data-cnstrc-item-name="isRecommendations ? product.name : undefined"
      :data-cnstrc-item-price="isRecommendations ? (product.special_price && product.price ? Math.min(product.special_price, product.price) : product.price || undefined) : undefined"
      :data-cnstrc-item-variation-id="isRecommendations ? (product.variationKey || product.childId || undefined) : undefined"
      :data-cnstrc-strategy-id="isRecommendations ? product.strategyId : undefined"
    )
      component.product-link(
        :is="product.url ? 'RogueLink' : 'div'"
        :to="product.url"
        :productId="product.hasRedirect ? '' : product.productId"
        :key="product.productId"
        @click="trackClick(product, index)"
        v-element-visibility="(isVisible) => trackViewProduct(index, isVisible)"
      )
        .product-img
          StaticImage(
            :src="product.thumbnail"
            :width="small ? 83 : 308"
            alt=""
            lazy
            fit-width
          )
          .snipe-img(v-if="listPromos[product.productId]")
            HotDealBadge.hot-deal-snipe(v-if="listPromos[product.productId].hasPromo" :label="__('Hot Deal')")
            StaticImage.img.hundo(v-else-if="listPromos[product.productId].hasHundo" src="/UI/logos/Hundo_Category_Snipe_s7bwil.svg" :width="224" :height="44" :alt="__('Hundo Pricing')")
        .product-name(:class="$slots.product && 'pad-b'")
          .title {{product.name || ' '}}
          hotdeal(v-if="displayHotDeals" :productId="product.productId" layout="carousel")
      slot(name="product" :product="product")
</template>
<script setup lang="ts">
import { vElementVisibility } from '@vueuse/components'
import { computed, type ComputedRef, defineAsyncComponent, inject, onMounted, onServerPrefetch, ref, watch } from 'vue'
import type { CatalogFilter, CatalogProduct, Rogue } from '../../js/sisuTypes'
import { useRogue } from '../../js/rogue'

// Async so it gets its own chunk instead of being bundled into the cms chunk group with this file
const HotDealBadge = defineAsyncComponent(() => import('../core/HotDealBadge.vue'))

const CATEGORY_WIDGET = 'rfkid_10'

const DISPLAY_COUNT = 12
// Deal lists drop items whose deal has ended, so they fetch a deeper pool to keep DISPLAY_COUNT on screen.
// Constructor recommendation pods don't page, so a backfill is a larger request (API max 100).
const DEAL_POOL_SIZES = [36, 100]

type DealFilter = 'hotdeal' | 'hundo' | null

// For analytics
const LIST_TYPES = {
  rfkid_32 : 'forYou',
  rfkid_31 : 'similar',
  rfkid_4  : 'cart',
  rfkid_10 : 'category',
}

// From App.vue
const activePromos = inject<ComputedRef<{ [productId: number]: any }>>('activePromos', computed(() => ({})))
const hundoProductIds = inject<ComputedRef<string[]>>('hundoProductIds', computed(() => []))
const refreshPromos = inject<((rogue: Rogue) => Promise<void>)>('refreshPromos', async () => {})

const rogue = useRogue()

const props = withDefaults(defineProps<{
  lazy?: boolean
  // Used for the sidecart images
  small?: boolean
  products?: CatalogProduct[] | null
  size?: string | null
  displayHotDeals?: boolean
  category?: string | null
  widget?: string
  filter?: CatalogFilter
  podId?: string
  itemIds?: string[]
  filterGroupIds?: string[]
  groupId?: string
}>(), {
  lazy            : false,
  small           : false,
  products        : null,
  size            : null,
  displayHotDeals : false,
  category        : null,
  widget          : 'rfkid_32',
})

const podId = computed(() => props.podId ?? (props.groupId ? undefined : 'preview-search'))

const results = ref<any[]>([{}, {}, {}, {}])
const visible = ref(false)
const loaded = ref(false)
const cnstrcResultId = ref<string | undefined>(undefined)
let loading: boolean = false
let trackOnLoad: number[] | undefined = undefined

const isRecommendations = computed(() => !props.products && !props.groupId)
const cnstrcNumResults = computed<number | undefined>(() => isRecommendations.value && loaded.value ? productList.value.length : undefined)

// Only the homepage deal carousels are filtered by live deal status, matched on the CMS category or the Constructor pod id
const dealFilter = computed<DealFilter>(() => {
  if (rogue.router?.currentRoute.value.path !== '/') return null

  const source = `${props.category || ''} ${props.podId || ''}`
  if (source.includes('hot-deals')) return 'hotdeal'
  if (source.includes('hundo')) return 'hundo'

  return null
})

const poolStep = ref(0)
const requestCount = computed(() => (dealFilter.value ? DEAL_POOL_SIZES[poolStep.value] : DISPLAY_COUNT))

function isOnDeal(product: any) {
  const promo = listPromos.value[product.productId]

  return dealFilter.value === 'hundo' ? !!promo?.hasHundo : !!promo?.hasPromo
}

const productList = computed(() => {
  if (!dealFilter.value) return results.value

  return results.value.filter(isOnDeal).slice(0, DISPLAY_COUNT)
})

const uri = computed(() => {
  if (props.category && rogue?.site?.code) {
    let url = props.category
    const code = rogue.site.code
    if (code === 'AUS') {
      url = url.replace('/featured', '/featured-au')
    } else if (code === 'EUR' || code === 'GBR') {
      url = url.replace('/featured', '/featured-eu')
    } else if (url !== '/featured') {
      url = url.replace('/featured', '/na-featured')
    }

    return url
  }

  return rogue.router?.currentRoute.value.fullPath
})

const listPromos = computed(() => {
  if (rogue && loaded.value) {
    const retval = {}
    const promos = activePromos.value
    const hundo = hundoProductIds.value

    results.value.forEach(value => {
      retval[value.productId] = { hasHundo: hundo.includes(`${value.productId}`), hasPromo: (promos[value.productId] || {}).status === 'active' }
    })

    return retval
  }

  return {}
})

const trackData = computed(() => {
  if (props.category) {
    return { listType: 'category', listEntity: { type: 'category', name: 'preview', url: props.category, id: 0 } }
  }

  return { listType: LIST_TYPES[props.widget] }
})

function trackViewProduct(index: number, isVisible?: boolean) {
  if (!isVisible) return

  // If we haven't loaded yet, we need to wait for that first
  if (!loaded.value) {
    trackOnLoad = trackOnLoad || []
    trackOnLoad.push(index)

    return
  }
  const product = productList.value[index]
  if (product && index !== undefined && ((rogue || {}).track || {}).listItemViewDebounce) {
    const data = Object.assign({
      type        : 'listView',
      rfkProducts : [{ product, index: index + 1 }],
    }, trackData.value)
    rogue.track.listItemViewDebounce(data)
  }
}

function trackClick(product: any, index: number) {
  if (product && index !== undefined && ((rogue || {}).track || {}).track) {
    const data = Object.assign({
      product,
      type     : 'productClick',
      position : index + 1,
    }, trackData.value)
    rogue.track.track(data)
  }
}

async function load() {
  // If the parent is controlling products (even if empty for a moment), do not fetch RFK
  if (props.products?.length) {
    results.value = props.products
    loaded.value = true

    return
  }
  if (props.products !== null) {
    return
  }

  if (loaded.value || (props.lazy && !visible.value) || !rogue || loading) {
    return
  }

  loading = true

  try {
    // Stays synchronous when the service answers from SSR state, so hydration renders the same cards
    const fetched = fetchProducts(requestCount.value)
    results.value = fetched instanceof Promise ? await fetched : fetched
  } catch {
    results.value = []
  }

  loading = false
  loaded.value = true

  // If the customer is already viewing this list, we may need to send "View" events
  // now that we are loaded
  if (trackOnLoad) {
    trackOnLoad.forEach(index => trackViewProduct(index))
    trackOnLoad = undefined
  }

  backfillIfShort()
}

function fetchProducts(count: number, fresh = false): any[] | Promise<any[]> {
  const useConstructor = rogue.featureFlags?.isOn('enable_constructor')

  let request: any

  if (useConstructor && podId.value) {
    const filters = props.filterGroupIds?.length ? { group_id: props.filterGroupIds } : undefined
    // Deal pods stay out of the shared SSR cache, so an ended deal is never served for the cache's full TTL
    const options = dealFilter.value ? { useSharedCache: false, fresh } : undefined
    request = rogue.cnstrc.getRecommendations(podId.value, count, undefined, props.itemIds, filters, options)
  } else if (useConstructor) {
    request = rogue.cnstrc.getBrowseResults('group_id', String(props.groupId!), { numResults: count })
  } else {
    const payload: any = { page_number: 1, uri: uri.value, n_item: count, widget: props.category ? CATEGORY_WIDGET : props.widget }
    if (props.filter) {
      payload.filter = props.filter
    }
    if (props.category) {
      payload.sort = { value: [{ type: 'featured', order: 'desc' }] }
    }

    request = rogue.rfk?.fetch(payload)
  }

  const toProducts = (response: any): any[] => {
    if (useConstructor) {
      cnstrcResultId.value = response?.result_id
    }

    return (((response?.content || {}).product || {}).value || [])
  }

  return request?.then ? request.then(toProducts) : toProducts(request)
}

// Replace the deal pool with a fresh fetch while keeping the current cards on screen until it lands
async function refetchDeals() {
  // Parent-provided products aren't ours to replace
  if (loading || props.products !== null) return

  loading = true

  try {
    results.value = await fetchProducts(requestCount.value, true)
  } catch (e) {
    rogue.log?.debug('Error refreshing deal carousel', e)
  }

  loading = false

  backfillIfShort()
}

// Ended deals shrink the list, so ask the pod for a deeper pool as long as the last request came back full
function backfillIfShort() {
  if (rogue.ssr || !dealFilter.value || !loaded.value || loading || props.products !== null) return
  if (productList.value.length >= DISPLAY_COUNT) return
  if (results.value.length < requestCount.value || poolStep.value >= DEAL_POOL_SIZES.length - 1) return

  poolStep.value += 1
  refetchDeals()
}

watch(() => productList.value.length, backfillIfShort)

onMounted(() => {
  // The page is CDN cached, so the SSR-supplied deal pool can predate deals that have since ended or started
  if (dealFilter.value && loaded.value) {
    refetchDeals()
  }
})

async function show(isVisible) {
  if (!isVisible) return

  visible.value = isVisible
  if (!loaded.value) {
    await load()
  }
}

watch(() => props.products, (val) => {
  if (val && val.length) {
    results.value = val
    loaded.value = true
  }
}, {
  immediate : true,
  deep      : true,
})

watch(() => props.filterGroupIds, () => {
  loaded.value = false
  loading = false
  results.value = [{}, {}, {}, {}]
  load()
}, { deep: true })

onServerPrefetch(async () => {
  await refreshPromos(rogue)
  await load()
})

if (!rogue.ssr) {
  load()
}
</script>
<style lang="scss" scoped>
.product-list {
  display: flex;
}

.product-name {
  font-family: $font-condensed;
  margin-top: $space-s;
  color: $color-black-light;

  &.pad-b {
    margin-bottom: $space-xxs;
  }

  .title {
    min-height: 38px;
    display: -webkit-box;
    overflow: hidden;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
}

.product-card {
  margin-right: $space-s;
  flex-shrink: 0;
  flex-grow: 0;
  width: 308px;
  &:last-child {
    margin-right: 0;
  }
}

.product-link {
  text-decoration: none;
}

.product-img {
  position:relative;
}

.img-size-medium {
  .product-card {
    width: 250px;
  }
  @include mobile(){
    .product-card {
      width: 145px;
    }
  }
}

.img-size-compact {
  .product-card {
    width: 150px;
  }
}

.img-size-small-category {
  .product-card {
    @include all-desktop() {
      width: 245px;
    }
    @include mobile() {
      width: 100%;
    }
  }
}

.snipe-img {
  position: absolute;
  bottom: 0;
  width:100%;

  .img {
    width: 50%;
    &.hundo {
      width: 62%;
    }
  }

  // Sized and inset to match the old snipe image, whose artwork had its padding built in
  .hot-deal-snipe {
    --hot-deal-badge-bg: #{$color-gray-x-light};
    width: 46%;
    margin: 0 0 2.5% 2%;
  }
}

@include mobile() {
  .reactive .product-card {
    width: 125px;
  }
}

.small {
  .product-name {
    font-size: $size-xxs;
    margin-top: $space-xs;
    font-family: $font-base;

    .title {
      min-height: 28px;
    }
  }

  .product-card {
    width: 83px;
  }
}

</style>
